import type { LinhaComComplementos } from './complementos-proposta.js';
export type TipoLinha = 'TT' | 'TT_PRE' | 'PORTABILIDADE' | 'NOVA' | 'FIBRA' | 'TV' | 'RENOVACAO' | 'LINHA_NOVA' | 'SERVICO';

export interface LinhaProposta extends LinhaComComplementos {
  tipo: TipoLinha;
  ddd?: string;
  plano?: { tipoPessoa?: 'PF' | 'PJ'; nome: string; gb?: number; valor?: number; velocidade?: string; produtoPedidoId?: string | null; mesesPromocionais?: number | null; valorAposPromocao?: number | null };
  tipoFibra?: 'CNPJ' | 'TT';
  tipoBloco?: 'NOVO' | 'INCREMENTO' | 'RENOVACAO';
}
export interface ComposicaoProposta {
  tipoPessoa?: 'PF' | 'PJ';
  apresentacao?: { ocultarPlanoAtual?: boolean; beneficiosDosProdutos?: string[]; chamada?: string };
  linhas: LinhaProposta[];
  totalGB: number;
  totalValor: number;
}
export interface LinhaDeHoje {
  numero?: string | null;
  plano?: string | null;
  valor: number;
}
export interface PlanoAtualProposta {
  qtdLinhas: number;
  gb?: number;
  fibra?: string;
  tv?: string;
  valor: number;
  linhasHoje?: LinhaDeHoje[];
  contaInteira?: boolean;
}
export interface GerarMensagemPropostaParams {
  nomeCliente?: string;
  razaoSocial?: string;
  planoAtual: PlanoAtualProposta;
  composicaoNova: ComposicaoProposta;
  consultorNome?: string;
  apresentarConsultor?: boolean;
  ocultarPlanoAtual?: boolean;
  beneficiosDosProdutos?: string[];
  servicosDigitais?: string[];
  aparelhos?: string[];
  passaports?: string[];
  pontosTV?: number;
}

/** A operação vem de cada item, nunca do tipo geral do lead. NOVA é bloco. */
export const OPERACOES_PROPOSTA: Record<TipoLinha, string> = {
  TT: 'Transferência de titularidade para CNPJ',
  TT_PRE: 'Transferência de titularidade pré-pago para CNPJ',
  PORTABILIDADE: 'Portabilidade', LINHA_NOVA: 'Linha nova',
  RENOVACAO: 'Renovação', NOVA: 'Bloco de dados',
  FIBRA: 'Fibra', TV: 'TV', SERVICO: 'Serviço digital',
};
export function ehLinhaDeVoz(tipo: string): boolean {
  return ['TT', 'TT_PRE', 'PORTABILIDADE', 'LINHA_NOVA', 'RENOVACAO'].includes(tipo);
}
export function identificarProdutoPedido(item: { plano_nome?: string | null; produto?: string | null }) {
  const nome = item.plano_nome?.trim() || item.produto?.trim() || 'Produto não informado';
  const operacao = String(item.produto || '').trim().toUpperCase();
  const bloco = /\bbloco(?:\s+de)?\s+dados\b/i.test(nome);
  const finalidade = bloco ? 'Pacote complementar entregue como uma linha nova para compartilhar dados com as linhas do cliente'
    : /^HA(?:\s|$)/.test(operacao) ? 'Linha móvel nova ou adicional; HA sozinho não identifica bloco de dados'
    : /^PN(?:\s|$)/.test(operacao) ? 'Portabilidade de linha de outra operadora para a Claro, mantendo o número'
    : /^TT(?:\s|$)/.test(operacao) ? 'Transferência de titularidade de linha já Claro; não é portabilidade nem novo chip'
    : /RENOVA/.test(operacao) ? 'Renovação de linha já existente na Claro'
    : /FIBRA/.test(operacao) ? 'Internet fixa; instalação e endereço próprios'
    : operacao === 'APARELHO' ? 'Aparelho vinculado ao pedido; passa por análise de crédito e logística próprias'
    : ['CLARO MONITOR','MCAFEE','MICROSOFT 365','GOOGLE WORKSPACE','MESH'].includes(operacao) ? 'Serviço adicional; não é linha móvel'
    : operacao.startsWith('PASSAPORT') ? 'Pacote adicional de roaming; não é linha móvel'
    : operacao === 'TV' ? 'Serviço de TV; instalação e equipamentos próprios'
    : 'Finalidade não classificada: conferir proposta original e pedido antes de explicar';
  return {
    nome_comercial: nome,
    tipo_operacional: item.produto || null,
    bloco_de_dados: bloco,
    referencia_numero: bloco ? 'Número de referência do bloco de dados' : 'Número do item',
    finalidade,
    regra_classificacao: bloco
      ? 'O número identifica a linha técnica do bloco; não é uma linha de voz pessoal. O pacote deve ser compartilhado pelo Gestor de Dados.'
      : /^HA(?:\s|$)/.test(operacao)
        ? 'Não chame de bloco apenas porque o produto é HA. Só é bloco quando o nome comercial ou a proposta comprovarem.'
        : null,
  };
}
export function quantidadesDaProposta(linhas: Array<{ tipo: string }>) {
  return { itens: linhas.length, voz: linhas.filter(l => ehLinhaDeVoz(l.tipo)).length,
    blocos: linhas.filter(l => l.tipo === 'NOVA').length,
    fibra: linhas.filter(l => l.tipo === 'FIBRA').length,
    tv: linhas.filter(l => l.tipo === 'TV').length,
    servicos: linhas.filter(l => l.tipo === 'SERVICO').length };
}
export function descricaoQuantidadesProposta(linhas: Array<{ tipo: string }>): string {
  const q = quantidadesDaProposta(linhas);
  return [[q.voz, 'linha(s) móvel(is)'], [q.blocos, 'bloco(s) de dados'], [q.fibra, 'fibra(s)'],
    [q.tv, 'TV(s)'], [q.servicos, 'serviço(s)']].filter(([n]) => Number(n) > 0).map(([n, nome]) => `${n} ${nome}`).join(' + ');
}
export function ehPropostaCpf(composicao: Pick<ComposicaoProposta, 'tipoPessoa' | 'linhas'>): boolean {
  return composicao.tipoPessoa === 'PF' || composicao.linhas.some(l => l.plano?.tipoPessoa === 'PF');
}
export function marcaDaProposta(composicao: Pick<ComposicaoProposta, 'tipoPessoa' | 'linhas'>) {
  return ehPropostaCpf(composicao)
    ? { cpf: true, empresa: 'Prime Conecta - Claro', operadora: 'Claro', cliente: 'você', documento: 'CPF', tv: 'Claro TV' }
    : { cpf: false, empresa: 'Empresarial Prime - Claro Empresas', operadora: 'Claro Empresas', cliente: 'sua empresa', documento: 'CNPJ', tv: 'Claro TV Empresas' };
}
export function promocaoDoPlano(plano?: LinhaProposta['plano']): string {
  const meses = Number(plano?.mesesPromocionais || 0);
  const depois = Number(plano?.valorAposPromocao || 0);
  if (!meses || !depois || depois === Number(plano?.valor || 0)) return '';
  return ` nos ${meses} primeiros meses, depois ${formatarMoedaPlano(depois)}/mês`;
}
export function operacoesDaProposta(composicao: ComposicaoProposta): string[] {
  const cpf = ehPropostaCpf(composicao);
  return [...new Set(composicao.linhas.map(l => cpf && (l.tipo === 'TT' || l.tipo === 'TT_PRE')
    ? (l.tipo === 'TT' ? 'Transferência de titularidade' : 'Pré-pago para pós')
    : l.tipo === 'FIBRA' && l.tipoFibra === 'TT'
    ? 'Transferência de titularidade da fibra' : l.tipo === 'NOVA' && l.tipoBloco === 'RENOVACAO'
      ? 'Renovação de bloco de dados' : l.tipo === 'NOVA' && l.tipoBloco === 'INCREMENTO'
        ? 'Incremento de bloco de dados' : OPERACOES_PROPOSTA[l.tipo]))].filter(Boolean);
}
// A praça do catálogo é régua interna de preço (Douglas 28/09/2026: "não precisa
// colocar mercado em desenvolvimento, o cliente não precisa saber disso"). O nome
// do catálogo CPF carrega o grupo — MD1, MD2, GM, Carta na Manga, Cidades Rubi —
// e ele saía inteiro na proposta e no fechamento.
// Rótulo interno do catálogo (praça, grupo de preço, modalidade) não vai ao cliente:
// "Controle 40 GB · no Multi" sai como "Controle 40 GB" (Douglas 29/09/2026).
const PRACA_INTERNA = /^(?:(?:RS\s+)?(?:GM|MD\s*\d?)|Mercado em Desenvolvimento(?:\s*\d)?|Grande Mercado|Carta na Manga|Cidades Rubi|no Multi(?:\s.*)?|Single|Padrão|Especial\+?|Rentabilização|área não cabeada|Troca de pacote)$/i;
export function nomeParaCliente(nome: string | null | undefined): string {
  return String(nome ?? '').split(' · ').filter((p) => !PRACA_INTERNA.test(p.trim())).join(' · ').trim();
}

export function descricaoFibraDaProposta(plano?: LinhaProposta['plano']): string {
  const nome = nomeParaCliente(plano?.velocidade || plano?.nome || '');
  return `Fibra ${nome.replace(/^(?:Claro\s+)?(?:Internet\s+)?Fibra\s*/i, '')}`.trim();
}

// Mega Bônus da convergência fibra + Claro Pós (CNPJ). O número é do BANCO
// (mega_bonus_convergencia), trocado em Configurações → Produtos → Planos e Preços
// (Douglas 09/10/2026). O backend carrega com lerMegaBonusGb() e o front pelo
// catálogo de planos; aqui fica só o valor em memória que as funções síncronas
// (proposta, fechamento, pedido) leem. 50 = Pré-Black Friday Claro (era 30).
let megaBonusGbAtual = 50;
export function megaBonusGb(): number {
  return megaBonusGbAtual;
}
export function definirMegaBonusGb(gb: unknown): void {
  // Number(null) e Number('') dão 0: só número de verdade troca o bônus.
  if (typeof gb !== 'number' && !(typeof gb === 'string' && /^\d+$/.test(gb.trim()))) return;
  const n = Number(gb);
  if (Number.isInteger(n) && n >= 0) megaBonusGbAtual = n;
}
// As IAs (IsabellA, Recuperação) escrevem este marcador no prompt e nos produtos;
// montarSystemPrompt troca pelo número do banco.
export const MARCADOR_MEGA_BONUS = '{{mega_bonus_gb}}';

export function megabonusDaProposta(composicao: ComposicaoProposta): number {
  // A oferta CPF não herda o benefício comercial do catálogo PJ.
  if (composicao.tipoPessoa === 'PF' || composicao.linhas.some(l => l.plano?.tipoPessoa === 'PF')) return 0;
  return composicao.linhas.some(l => ehLinhaDeVoz(l.tipo) || l.tipo === 'NOVA')
    && composicao.linhas.some(l => l.tipo === 'FIBRA' && l.tipoFibra !== 'TT') ? megaBonusGb() : 0;
}

// = formatarMoedaPlano (planosCNPJ.ts:901) — mesmo Intl, mesma saida.
export function formatarMoedaPlano(valor: number): string {
  if (!Number.isFinite(valor)) throw new Error('Valor monetário inválido na proposta; confira a composição antes de enviar.');
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);
}

/** Primeiro nome, com a inicial maiúscula ("RODRIGO PAZ" → "Rodrigo"). */
export function primeiroNome(nome?: string | null): string {
  const p = String(nome || '').trim().split(/\s+/)[0] || '';
  return p ? p.charAt(0).toUpperCase() + p.slice(1).toLowerCase() : '';
}

function telefoneDaLinha(numero?: unknown): string {
  const d = String(numero ?? '').replace(/\D/g, '').replace(/^55(?=\d{10,11}$)/, '');
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return '';
}

/**
 * ⛔ PROPOSTA CPF = COMPARATIVO (Douglas 25/09/2026, texto aprovado).
 * "Hoje você tem" × "Proposta", linha por linha, com o total de cada lado.
 *
 * A CONTA INTEIRA E A ECONOMIA (Douglas 03/10/2026, "ajuste tudo"): o "hoje"
 * mostrava só as linhas que entravam na proposta, e em 43 de 71 conversas da
 * PORT CPF em que a prévia dava economia a tabela dizia que ficava mais caro
 * (as linhas paradas saíam da proposta e da conta de hoje juntas). Com
 * linhasHoje, o "hoje" é a conta inteira, as que ficam de fora aparecem, e a
 * diferença sai escrita — o mesmo número da prévia, nunca outro.
 */
export function gerarPropostaCpfComparativo(params: GerarMensagemPropostaParams): string {
  const { composicaoNova, planoAtual, ocultarPlanoAtual } = params;
  // Na PF a razão social do lead é a empresa de onde vieram as linhas (o
  // "Xpto, aqui está o comparativo" de 29/09): só vira nome se parecer pessoa.
  const razaoEhPessoa = !!params.razaoSocial && !/\d|\b(ltda|eireli|me|epp|s\/?a|mei|comercio|servi[cç]os|informatica|xpto)\b/i.test(params.razaoSocial);
  const nome = primeiroNome(params.nomeCliente || (razaoEhPessoa ? params.razaoSocial : ''));
  const linhas = composicaoNova.linhas as Array<LinhaProposta & Record<string, unknown>>;
  const comAtual = linhas.filter((l) => ehLinhaDeVoz(l.tipo) && Number(l.valorAtual || 0) > 0);
  const linhasHoje = (planoAtual.linhasHoje ?? []).filter((l) => Number(l.valor) > 0);
  const mostraHoje = !ocultarPlanoAtual && (linhasHoje.length > 0 || comAtual.length > 0 || planoAtual.valor > 0);
  // Linha que vem de outra operadora não é "conta Claro" (Douglas 29/09/2026), e
  // sem o "hoje" não há o que comparar: é a proposta.
  const deOutraOperadora = linhas.some((l) => l.tipo === 'PORTABILIDADE');
  const daClaro = linhas.some((l) => l.tipo === 'RENOVACAO' || l.tipo === 'TT_PRE');
  // O MULTI PRIMEIRO (Douglas 14/10/2026, aprovado): "a IA não sabe que tem que sempre já
  // iniciar a conversa vendendo o Multi, nunca apenas a linha". Caso Luiz (2610070064): o
  // "comparativo das suas linhas" pôs a fibra como mais um item, ele respondeu "Ok pode" e,
  // com o pedido feito, "só a portabilidade". Com a fibra e o celular, a tabela é o Multi.
  const multi = ehMultiCpf(linhas);
  const fibraNova = !planoAtual.fibra
    && !linhas.some((l) => l.tipo === 'FIBRA' && /migra/i.test(String((l.plano as Record<string, unknown> | undefined)?.id ?? l.planoId ?? '')));
  const titulo = multi ? 'o seu Multi Claro: a internet de casa e o celular numa conta só'
    : !mostraHoje ? 'a sua proposta' : deOutraOperadora ? 'o comparativo das suas linhas' : daClaro ? 'o comparativo da sua conta Claro' : 'o comparativo';
  const partes: string[] = [`${nome ? `${nome}, aqui` : 'Aqui'} está ${titulo}:`, ''];

  let totalHoje = 0;
  if (mostraHoje) {
    partes.push('*Hoje você tem:*');
    if (linhasHoje.length > 0) {
      const naProposta = new Set(linhas.filter((l) => ehLinhaDeVoz(l.tipo)).map((l) => String(l.numero ?? '').replace(/\D/g, '').slice(-8)));
      const entra = (l: LinhaDeHoje) => naProposta.has(String(l.numero ?? '').replace(/\D/g, '').slice(-8));
      const rotulo = (l: LinhaDeHoje) => [telefoneDaLinha(l.numero), String(l.plano || '').trim() || 'Plano atual'].filter(Boolean).join(' · ');
      for (const l of linhasHoje.filter(entra)) partes.push(`• ${rotulo(l)}: ${formatarMoedaPlano(Number(l.valor))}`);
      const fora = linhasHoje.filter((l) => !entra(l));
      if (fora.length <= 2) {
        for (const l of fora) partes.push(`• ${rotulo(l)}: ${formatarMoedaPlano(Number(l.valor))} (fica de fora)`);
      } else {
        partes.push(`• Mais ${fora.length} linhas que ficam de fora: ${formatarMoedaPlano(fora.reduce((s, l) => s + Number(l.valor), 0))}`);
      }
      totalHoje = linhasHoje.reduce((s, l) => s + Number(l.valor), 0);
    } else if (comAtual.length > 0) {
      for (const l of comAtual) {
        const fone = telefoneDaLinha(l.numero);
        partes.push(`• ${[fone, String(l.planoAtual || '').trim() || 'Plano atual'].filter(Boolean).join(' · ')}: ${formatarMoedaPlano(Number(l.valorAtual))}`);
      }
      totalHoje = comAtual.reduce((s, l) => s + Number(l.valorAtual || 0), 0);
    } else {
      if (planoAtual.qtdLinhas > 0) partes.push(`• ${planoAtual.qtdLinhas} linha(s)${planoAtual.gb ? ` · ${planoAtual.gb}GB` : ''}`);
      totalHoje = planoAtual.valor;
    }
    if (planoAtual.fibra) partes.push(`• Internet ${planoAtual.fibra}`);
    if (planoAtual.tv) partes.push(`• TV ${planoAtual.tv}`);
    totalHoje = Math.round(totalHoje * 100) / 100;
    partes.push(`Total: ${formatarMoedaPlano(totalHoje)}/mês`, '');
  }

  partes.push(multi ? '*No Multi:*' : '*Proposta:*');
  partes.push(...itensDaPropostaCpf(composicaoNova, multi ? { multi: true, fibraNova } : undefined));
  partes.push(...(params.servicosDigitais ?? []));
  for (const ap of params.aparelhos ?? []) partes.push(`• ${ap}`);
  for (const n of params.passaports ?? []) partes.push(`• ${n} (roaming internacional)`);
  partes.push(`Total: ${formatarMoedaPlano(composicaoNova.totalValor)}/mês`, '');
  const diferenca = mostraHoje && planoAtual.contaInteira
    ? diferencaDaConta(totalHoje, linhas, composicaoNova.totalValor, multi && fibraNova) : null;
  if (diferenca) partes.push(diferenca, '');
  // A Conecta grava chamada '' e fecha com a pergunta dela, no tom da campanha:
  // tabela e mensagem terminando cada uma com uma pergunta eram duas perguntas.
  const chamada = composicaoNova.apresentacao?.chamada;
  if (chamada !== '') partes.push(chamada?.trim() || 'Posso seguir com a atualização?');
  return partes.join('\n').trimEnd();
}

/** A diferença em uma frase, com o período da promoção quando existe. */
function diferencaDaConta(totalHoje: number, linhas: Array<LinhaProposta & Record<string, unknown>>, totalValor: number,
  ganhaInternet = false): string | null {
  if (!(totalHoje > 0)) return null;
  const centavos = (v: number) => Math.round(v * 100) / 100;
  const totalDepois = centavos(linhas.reduce((s, l) => s + Number(l.plano?.valorAposPromocao || l.plano?.valor || 0), 0));
  const meses = Math.max(0, ...linhas.map((l) => (Number(l.plano?.valorAposPromocao || 0) > Number(l.plano?.valor || 0) ? Number(l.plano?.mesesPromocionais || 0) : 0)));
  const agora = centavos(totalHoje - totalValor);
  const depois = centavos(totalHoje - totalDepois);
  const temFibra = linhas.some((l) => l.tipo === 'FIBRA');
  const ganha = ganhaInternet ? ' e ainda ganha a internet de casa' : '';
  if (agora > 0) {
    if (meses > 0 && depois !== agora) {
      return depois > 0
        ? `*Você paga ${formatarMoedaPlano(agora)} a menos por mês nos ${meses} primeiros meses e ${formatarMoedaPlano(depois)} a menos depois${ganha ? `,${ganha}` : ''}.*`
        : `*Você paga ${formatarMoedaPlano(agora)} a menos por mês nos ${meses} primeiros meses; depois a conta fica ${formatarMoedaPlano(totalDepois)}.*`;
    }
    return `*Você paga ${formatarMoedaPlano(agora)} a menos por mês (${formatarMoedaPlano(centavos(agora * 12))} em 12 meses)${ganha}.*`;
  }
  if (agora < 0) return `*São ${formatarMoedaPlano(-agora)} a mais por mês${temFibra ? ', já com a internet de casa' : ''}.*`;
  return null;
}

// No CPF não existe "para CNPJ": a operação é da pessoa física.
const OPERACOES_CPF: Partial<Record<TipoLinha, string>> = {
  TT: 'Transferência de titularidade', TT_PRE: 'Pré-pago para pós',
  PORTABILIDADE: 'Portabilidade', LINHA_NOVA: 'Linha nova', RENOVACAO: 'Renovação', NOVA: 'Bloco de dados',
};

/** A fibra e o celular na mesma proposta CPF: é o Multi. */
export function ehMultiCpf(linhas: Array<{ tipo: string }>): boolean {
  return linhas.some((l) => l.tipo === 'FIBRA') && linhas.some((l) => ehLinhaDeVoz(l.tipo) || l.tipo === 'NOVA');
}

/**
 * Uma linha por produto da proposta CPF — a mesma lista no comparativo e no fechamento.
 * No Multi (só a tabela da proposta) a internet de casa vem primeiro e o celular de
 * outra operadora sai "com o mesmo número"; `fibraNova` diz que ela será instalada.
 */
export function itensDaPropostaCpf(composicao: Pick<ComposicaoProposta, 'linhas'>,
  opcoes?: { multi?: boolean; fibraNova?: boolean }): string[] {
  const todas = composicao.linhas as Array<LinhaProposta & Record<string, unknown>>;
  const multi = !!opcoes?.multi;
  const daCasa = (l: { tipo: string }) => (!multi ? 0 : l.tipo === 'FIBRA' ? 0 : l.tipo === 'TV' ? 1 : 2);
  // Dependente logo abaixo do titular (Douglas 29/09/2026): o cliente lê "um plano
  // da família, a internet dividida" — não uma lista de planos soltos.
  const chave = (l: Record<string, unknown>) => String(l.linha_id || l.id || '');
  const dependentes = todas.filter((l) => l.titularLinhaId);
  const linhas = todas.filter((l) => !l.titularLinhaId).sort((a, b) => daCasa(a) - daCasa(b))
    .flatMap((t) => [t, ...dependentes.filter((d) => d.titularLinhaId === chave(t))]);
  linhas.push(...dependentes.filter((d) => !linhas.includes(d)));
  const partes: string[] = [];
  for (const l of linhas) {
    const valor = Number(l.plano?.valor || 0);
    if (l.titularLinhaId) {
      const dep = (l.plano as Record<string, unknown> | undefined)?.dependente;
      const operacao = l.tipo === 'RENOVACAO' ? '' : l.tipo === 'NOVA' || l.tipo === 'LINHA_NOVA' ? 'Linha nova' : OPERACOES_CPF[l.tipo] || '';
      const rotulo = [telefoneDaLinha(l.numero), operacao].filter(Boolean).join(' · ') || 'Linha';
      const oque = dep === 'dados' ? 'dependente só dados, divide a internet do plano' : 'dependente, divide a internet do plano';
      partes.push(`   ↳ ${rotulo} · ${oque}: ${dep === 'incluso' ? 'incluída no plano' : formatarMoedaPlano(valor)}`);
      continue;
    }
    const preco = valor > 0 ? `: ${formatarMoedaPlano(valor)}${promocaoDoPlano(l.plano)}` : '';
    if (ehLinhaDeVoz(l.tipo) || l.tipo === 'NOVA') {
      const operacao = l.tipo === 'RENOVACAO' ? '' : OPERACOES_CPF[l.tipo] || '';
      const fone = telefoneDaLinha(l.numero);
      const rotulo = !multi || l.tipo === 'NOVA'
        ? [fone, operacao].filter(Boolean).join(' · ') || 'Linha'
        : l.tipo === 'PORTABILIDADE'
          ? `Celular${fone ? ` ${fone}` : ''}, com o mesmo número`
          : [`Celular${fone ? ` ${fone}` : ''}`, operacao].filter(Boolean).join(' · ');
      partes.push(`• ${rotulo} · ${nomeParaCliente(l.plano?.nome) || 'Plano'}${preco}`);
    } else if (l.tipo === 'FIBRA') {
      partes.push(multi
        ? `• Internet de casa · ${descricaoFibraDaProposta(l.plano)}${opcoes?.fibraNova ? ', instalada no seu endereço' : ''}${preco}`
        : `• ${descricaoFibraDaProposta(l.plano)}${preco}`);
    } else if (l.tipo === 'TV') {
      const tv = nomeParaCliente(l.plano?.nome);
      partes.push(`• ${/^claro\s+tv/i.test(tv) || /^tv\b/i.test(tv) ? tv : `TV ${tv}`}${preco}`);
    }
  }
  return partes;
}

export function gerarMensagemPropostaCompleta(params: GerarMensagemPropostaParams): string {
  const { nomeCliente, razaoSocial, planoAtual, composicaoNova, consultorNome, ocultarPlanoAtual } = params;
  if (ehPropostaCpf(params.composicaoNova)) return gerarPropostaCpfComparativo(params);

  const economia = planoAtual.valor - composicaoNova.totalValor;
  const economiaPercent = planoAtual.valor > 0 ? Math.round((economia / planoAtual.valor) * 100) : 0;

  const linhaFibra = composicaoNova.linhas.find((l) => l.tipo === 'FIBRA');
  const megabonus = megabonusDaProposta(composicaoNova);
  const fibraNovaTexto = linhaFibra ? descricaoFibraDaProposta(linhaFibra.plano) : null;
  const linhaTV = composicaoNova.linhas.find((l) => l.tipo === 'TV');
  const tvNovaTexto = linhaTV?.plano?.nome ? `${linhaTV.plano.nome}${params.pontosTV ? ` + ${params.pontosTV} Ponto(s) Adicional(is)` : ''}` : null;

  const marca = marcaDaProposta(composicaoNova);
  const nomeEmpresa = razaoSocial || nomeCliente || marca.cliente;

  const linhasMoveis = composicaoNova.linhas.filter((l) =>
    l.tipo === 'TT' || l.tipo === 'TT_PRE' || l.tipo === 'PORTABILIDADE' || l.tipo === 'RENOVACAO' || l.tipo === 'LINHA_NOVA');
  const qtdLinhasNovas = linhasMoveis.length;
  const temBloco = composicaoNova.linhas.some((l) => l.tipo === 'NOVA');
  const temMovel = qtdLinhasNovas > 0 || temBloco;
  const temFibra = !!linhaFibra;
  const megabonusAplicavel = temMovel ? megabonus : 0;
  const gbMovelExibir = composicaoNova.totalGB + megabonusAplicavel;

  const partes: string[] = [];
  if (params.apresentarConsultor !== false) {
    partes.push(`Olá! Sou *${consultorNome || 'Consultor'}*, da *${marca.empresa}*.`);
    partes.push('');
  }
  partes.push(`Preparei uma proposta especial para *${nomeEmpresa}*.`);
  partes.push('');

  if (!ocultarPlanoAtual && planoAtual.valor > 0) {
    partes.push('*Seu plano hoje:*');
    if (planoAtual.qtdLinhas > 0) partes.push(`• ${planoAtual.qtdLinhas} linha(s)`);
    if (planoAtual.gb) partes.push(`• ${planoAtual.gb}GB de internet`);
    if (planoAtual.fibra) partes.push(`• Fibra ${planoAtual.fibra}`);
    if (planoAtual.tv) partes.push(`• TV ${planoAtual.tv}`);
    partes.push(`• Valor: ${formatarMoedaPlano(planoAtual.valor)}/mês`);
    partes.push('');
  }

  partes.push(`*Na ${marca.operadora} você terá:*`);
  const operacoes = operacoesDaProposta(composicaoNova).filter(o => !['Fibra', 'TV'].includes(o));
  if (operacoes.length) partes.push(`• ${operacoes.join(' · ')}`);
  if (qtdLinhasNovas > 0) partes.push(`• ${qtdLinhasNovas} linha(s) móvel(is)`);
  if (temMovel && gbMovelExibir > 0) partes.push(`• Até ${gbMovelExibir}GB de internet móvel`);
  if (temBloco || (marca.cpf && composicaoNova.linhas.some(l => promocaoDoPlano(l.plano)))) {
    const grupos = new Map<string, { nome: string; quantidade: number; valor: number; ddd?: string; promo: string }>();
    for (const linha of composicaoNova.linhas.filter(l => ehLinhaDeVoz(l.tipo) || l.tipo === 'NOVA')) {
      const nome = linha.plano?.nome || 'Plano';
      const valor = Number(linha.plano?.valor || 0);
      const ddd = linha.ddd;
      const promo = promocaoDoPlano(linha.plano);
      const chave = JSON.stringify([linha.tipo, nome, valor, ddd, promo]);
      const grupo = grupos.get(chave);
      if (grupo) grupo.quantidade += 1;
      else grupos.set(chave, { nome, valor, ddd, quantidade: 1, promo });
    }
    for (const g of grupos.values()) partes.push(`• ${g.quantidade} × ${g.nome}${g.ddd ? ` · DDD ${g.ddd}` : ''}: ${formatarMoedaPlano(g.valor)} cada${g.promo} (${formatarMoedaPlano(g.valor * g.quantidade)}/mês)`);
  }

  if (fibraNovaTexto) partes.push(`• ${fibraNovaTexto}${marca.cpf && linhaFibra?.plano?.valor ? `: ${formatarMoedaPlano(Number(linhaFibra.plano.valor))}${promocaoDoPlano(linhaFibra.plano)}` : ''}`);
  if (tvNovaTexto) partes.push(`• TV ${tvNovaTexto}${marca.cpf && linhaTV?.plano?.valor ? `: ${formatarMoedaPlano(Number(linhaTV.plano.valor))}${promocaoDoPlano(linhaTV.plano)}` : ''}`);
  partes.push(...(params.servicosDigitais ?? []));
  for (const ap of params.aparelhos ?? []) partes.push(`• ${ap}`);
  for (const nome of params.passaports ?? []) partes.push(`• ${nome} (roaming internacional)`);
  partes.push(`• Valor: ${formatarMoedaPlano(composicaoNova.totalValor)}/mês`);
  partes.push('');

  const beneficios = [...new Set(params.beneficiosDosProdutos ?? composicaoNova.apresentacao?.beneficiosDosProdutos ?? [])]
    .map(b => b.trim()).filter(Boolean);
  if (megabonusAplicavel > 0) beneficios.push(`+${megabonusAplicavel}GB Megabônus com Fibra`);
  if (beneficios.length) {
    partes.push('*Benefícios inclusos:*');
    for (const b of beneficios) partes.push(`• ${b}`);
    partes.push('');
  }

  if (!ocultarPlanoAtual && economia > 0) {
    partes.push(`*Economia de ${formatarMoedaPlano(economia)}/mês (${economiaPercent}%)!*`);
    if (temMovel && Number(planoAtual.gb) > 0 && gbMovelExibir > Number(planoAtual.gb)) partes.push('Mais internet móvel e economia!');
  } else if (!ocultarPlanoAtual && planoAtual.valor > 0) {
    if (economia === 0) partes.push('*Mesmo valor mensal*, com os serviços descritos acima.');
    else partes.push(`Investimento adicional de ${formatarMoedaPlano(-economia)}/mês pelos serviços descritos acima.`);
  } else {
    partes.push('Condições conforme os produtos descritos acima.');
  }
  partes.push('');

  if (temFibra && temMovel) {
    const fibraCta = fibraNovaTexto ? `Internet ${fibraNovaTexto}` : 'Internet Fibra';
    partes.push(`*${fibraCta} + serviços móveis Claro* nesta proposta${megabonusAplicavel > 0 ? `, com +${megabonusAplicavel}GB de bônus na conta móvel` : ''}. Podemos seguir com esta proposta?`);
  } else if (temFibra) {
    const fibraCta = fibraNovaTexto ? `Internet ${fibraNovaTexto}` : 'Internet Fibra';
    partes.push(`*${fibraCta}* de alta velocidade para ${marca.cpf ? 'sua casa' : 'sua empresa'}. Podemos seguir com esta proposta?`);
  } else {
    partes.push(marca.cpf
      ? '*Claro* para você e sua família, com os planos e benefícios acima. Podemos seguir com esta proposta?'
      : '*Claro Empresas* para sua equipe, com os planos e benefícios acima. Podemos seguir com esta proposta?');
  }
  return partes.join('\n');
}

// A proposta que a IA grava no lead e' plana (tipo_proposta + plano_nome + totais).
// Esta funcao a reconstroi na forma que o gerador espera. Um tipo por proposta:
// convergencia e' DOIS pedidos (medido: 141 casos em 60 dias), nunca um snapshot.
const TIPO_PROPOSTA_PARA_LINHA: Record<string, TipoLinha> = {
  portabilidade_pj: 'PORTABILIDADE', portabilidade_pf: 'PORTABILIDADE',
  transferencia_titularidade_pos: 'TT', transferencia_titularidade_pre: 'TT_PRE',
  aquisicao_nova: 'LINHA_NOVA', aquisicao_incremento: 'LINHA_NOVA',
  renovacao: 'RENOVACAO', bloco_renovacao: 'NOVA', fibra: 'FIBRA', tv: 'TV',
};
export function composicaoDoSnapshotDaIA(snap: any, lead: {
  tipo_pessoa?: string | null;
  proposta_composicao_cnpj?: any;
  qtd_linhas_voz?: number | null; proposta_gb_atual?: number | null; valor_fatura_atual?: number | null;
  proposta_fibra_atual?: string | null; proposta_tv_atual?: string | null;
  proposta_linhas_novo?: number | null; proposta_gb_novo?: number | null; valor_proposta?: number | null;
}): { planoAtual: PlanoAtualProposta; composicaoNova: ComposicaoProposta } {
  const tipoEsperado = TIPO_PROPOSTA_PARA_LINHA[String(snap?.tipo_proposta ?? '').toLowerCase()];
  const canonica = lead.proposta_composicao_cnpj;
  const composicao = canonica?.linhas?.length && (!tipoEsperado || canonica.linhas.some((l: LinhaProposta) => l.tipo === tipoEsperado))
    ? canonica : snap?.composicao;
  if (composicao?.linhas?.length) {
    return { planoAtual: composicao.planoAtual ?? { qtdLinhas: Number(lead.qtd_linhas_voz ?? 0), gb: lead.proposta_gb_atual ?? undefined,
      fibra: lead.proposta_fibra_atual ?? undefined, tv: lead.proposta_tv_atual ?? undefined,
      valor: Number(lead.valor_fatura_atual ?? 0) }, composicaoNova: { ...composicao, tipoPessoa: lead.tipo_pessoa === 'PF' ? 'PF' : composicao.tipoPessoa } };
  }
  const tipo = TIPO_PROPOSTA_PARA_LINHA[String(snap?.tipo_proposta ?? '').toLowerCase()];
  if (!tipo) throw new Error('Proposta sem tipo válido: monte a proposta antes de enviar.');
  const n = Number(snap?.qtde_linhas_novo ?? lead.proposta_linhas_novo ?? 0);
  if (!Number.isInteger(n) || n < 1 || n > 1000) throw new Error('Quantidade de linhas inválida na proposta.');
  const totalValor = Number(snap?.valor_total_mensal ?? lead.valor_proposta ?? 0);
  if (!Number.isFinite(totalValor) || totalValor <= 0) throw new Error('Proposta sem valor válido.');
  const totalGB = Number(snap?.gb_novo ?? lead.proposta_gb_novo ?? 0);
  const planoNome = String(snap?.plano_nome ?? '');
  const ehFixa = tipo === 'FIBRA' || tipo === 'TV';
  const linhas: LinhaProposta[] = Array.from({ length: ehFixa ? 1 : n }, () => ({
    tipo,
    plano: { nome: planoNome, gb: ehFixa ? undefined : Math.round(totalGB / n), valor: totalValor / (ehFixa ? 1 : n),
             velocidade: tipo === 'FIBRA' ? planoNome.replace(/^Claro Fibra\s*/i, '').replace(/\s*Convergente$/i, '') || undefined : undefined },
  }));
  return {
    planoAtual: {
      qtdLinhas: Number(lead.qtd_linhas_voz ?? 0), gb: lead.proposta_gb_atual ?? undefined,
      fibra: lead.proposta_fibra_atual ?? undefined, tv: lead.proposta_tv_atual ?? undefined,
      valor: Number(lead.valor_fatura_atual ?? 0),
    },
    composicaoNova: { linhas, totalGB, totalValor },
  };
}

/**
 * A proposta CPF na linha oficial sai no modelo aprovado cpf_proposta_comparativo
 * (Douglas 28/09/2026) — o mesmo comparativo aprovado na conversa, e não o modelo
 * da Claro Empresas. Os cinco parâmetros saem do texto do gerador, bloco a bloco;
 * parâmetro de modelo não aceita quebra de linha, então os itens vão com " • ".
 */
export function paramsComparativoCpf(texto: string, nome: string): Record<string, string> | null {
  const linhas = texto.split('\n').map((l) => l.trim());
  const bloco = (titulo: string) => {
    const i = linhas.indexOf(titulo);
    if (i < 0) return null;
    const itens: string[] = []; let total = '';
    for (const l of linhas.slice(i + 1)) {
      if (l.startsWith('Total: ')) { total = l.slice(7).replace(/\/mês$/, '').trim(); break; }
      if (l) itens.push(l.replace(/^•\s*/, ''));
    }
    return itens.length && total ? { itens: itens.join(' • '), total } : null;
  };
  const proposta = bloco('*Proposta:*') ?? bloco('*No Multi:*');
  if (!proposta) return null;
  const hoje = bloco('*Hoje você tem:*');
  return {
    '1': nome || 'tudo bem',
    '2': hoje?.itens ?? 'conta atual não informada',
    '3': hoje?.total ?? '—',
    '4': proposta.itens,
    '5': proposta.total,
  };
}

/** Mesmo conteúdo comercial no parâmetro do template; a Meta fornece o cabeçalho. */
export function conteudoPropostaNoTemplateOficial(texto: string): string {
  const blocos = texto.trim().split(/\n\s*\n/);
  if (blocos[0]?.startsWith('Olá! Sou ')) blocos.shift();
  if (blocos[0]?.startsWith('Preparei uma proposta especial para ')) blocos.shift();
  // CPF: a saudação do comparativo sai — o modelo oficial tem a própria.
  if (/está (?:o comparativo(?: da sua conta Claro| das suas linhas)?|a sua proposta|o seu Multi Claro: a internet de casa e o celular numa conta só):$/.test(blocos[0] || '')) blocos.shift();
  // Parâmetros de template usam texto corrido; não descartar itens ou condições.
  return blocos.join(' • ').replace(/\s+/g, ' ').trim();
}
